import { computed, onMounted, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import paymentMethodService, { type PaymentMethod } from "@/services/paymentMethod";
import reportService, { type PurchasePaymentReportItem } from "@/services/report";
import supplierService, { type Supplier } from "@/services/supplier";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type PurchasePaymentFilters = {
  warehouse_id: number | null;
  supplier_id: number | null;
  method_id: number | null;
  from_date: string;
  to_date: string;
  per_page: number;
};

const createInitialFilters = (): PurchasePaymentFilters => ({
  warehouse_id: null,
  supplier_id: null,
  method_id: null,
  from_date: "",
  to_date: "",
  per_page: 10,
});

export function usePurchasePaymentReport() {
  const { t } = useI18n();
  const payments = ref<PurchasePaymentReportItem[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const suppliers = ref<Supplier[]>([]);
  const paymentMethods = ref<PaymentMethod[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const searchQuery = ref("");
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<PurchasePaymentFilters>(createInitialFilters());

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page,
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t("purchase_payment_report_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    locale: { items_per_page: t("purchase_payment_report_page.items_per_page") },
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value,
    warehouse_id: filters.warehouse_id || undefined,
    supplier_id: filters.supplier_id || undefined,
    method_id: filters.method_id || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
  }));

  const fetchFilterOptions = async () => {
    try {
      const [warehouseResults, supplierResults, paymentMethodResults] = await Promise.all([
        warehouseService.getWarehouses(),
        supplierService.getSuppliers({ per_page: 1000 }),
        paymentMethodService.getPaymentMethods(),
      ]);

      warehouses.value = warehouseResults;
      suppliers.value = supplierResults.data ?? [];
      paymentMethods.value = paymentMethodResults;
    } catch (error) {
      console.error("Error loading purchase payment report filters:", error);
    }
  };

  const fetchPurchasePaymentReports = async () => {
    loading.value = true;
    try {
      const response = await reportService.getPurchasePaymentReports(queryParams.value);
      payments.value = response.data;
      currentPage.value = response.current_page;
      total.value = response.total;
    } catch (error) {
      console.error("Error fetching purchase payment reports:", error);
      Swal.fire({
        icon: "error",
        title: t("purchase_payment_report_page.load_failed"),
        text: t("purchase_payment_report_page.unable_to_load_purchase_payment_reports"),
      });
    } finally {
      loading.value = false;
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    currentPage.value = page;

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize;
      currentPage.value = 1;
    }

    fetchPurchasePaymentReports();
  };

  const applyFilters = () => {
    currentPage.value = 1;
    fetchPurchasePaymentReports();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    currentPage.value = 1;
    fetchPurchasePaymentReports();
  };

  const exportPurchasePaymentReports = async () => {
    exporting.value = true;
    try {
      const response = await reportService.exportPurchasePaymentReports({
        search_query: searchQuery.value,
        warehouse_id: filters.warehouse_id || undefined,
        supplier_id: filters.supplier_id || undefined,
        method_id: filters.method_id || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `purchase_payment_report_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting purchase payment reports:", error);
      Swal.fire({
        icon: "error",
        title: t("purchase_payment_report_page.export_failed"),
        text: t("purchase_payment_report_page.unable_to_export_purchase_payment_reports"),
      });
    } finally {
      exporting.value = false;
    }
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchPurchasePaymentReports();
  });

  onMounted(async () => {
    await fetchFilterOptions();
    await fetchPurchasePaymentReports();
  });

  return {
    payments,
    warehouses,
    suppliers,
    paymentMethods,
    loading,
    exporting,
    searchQuery,
    filters,
    pagination,
    handleTableChange,
    applyFilters,
    resetFilters,
    exportPurchasePaymentReports,
  };
}
